import { replace } from 'connected-react-router'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import type { InViewHookResponse } from 'react-intersection-observer'
import { useInView } from 'react-intersection-observer'
import { useLocation } from 'react-router'
import { useIntl } from 'react-intl'

import { fetchCalendarForBedbankOffer, fetchRatesForBedbankOffer } from 'actions/BedbankOfferActions'
import { fetchFlightCalendar } from 'actions/FlightActions'
import { fetchRelatedTravelItems } from 'actions/OfferActions'
import * as Analytics from 'analytics/analytics'
import {
  offerPageDurationChangeEvent,
  offerPageGuestSelectionEvent,
  offerPageHotLeadEvent,
} from 'analytics/snowplow/events'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import BedbankOfferContent from 'components/OfferPage/Bedbank/BedbankOfferContent'
import bedbankMobileFilterMiddleware from 'components/OfferPage/Bedbank/MobileBookingFlow/BedbankMobileFilterMiddleware'
import { getNextFilterWithCapacityCheckInMiddleware } from 'components/OfferPage/utils'
import HotelStructuredData from 'components/Pages/BedbankOfferPage/HotelStructuredData'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import config from 'constants/config'
import { urlHashDatePicker } from 'constants/url'
import { MetaOffersDiscountProvider } from 'contexts/MetaOffersDiscountContext'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type { OfferPageDispatchAction, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useMetasearchSourceTransition from 'hooks/OfferPage/useMetasearchSourceTransition'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useMetaOffersDiscount } from 'hooks/useMetaOffersDiscount'
import useMountedEffect from 'hooks/useMountedEffect'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { getPromotionDefaultPeriod } from 'lib/hotels/bedbankUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { getDefaultAirport, selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import { getPromotionMinLos } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { getInitialBedbankOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useSyncBedbankTripItem } from 'tripPlanner/hooks/bookmarks/useSyncBedbankTripItem'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [bedbankMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]

interface Props {
  offerId: string
  offer: App.BedbankOffer
  lereYmalStickyFooterShowTrigger: InViewHookResponse
  footerElement: HTMLElement | null
}

function BedbankOfferContainer(props: Props) {
  const intl = useIntl()
  const {
    offerId,
    offer,
    lereYmalStickyFooterShowTrigger: ymalStickyFooterShowTrigger,
    footerElement,
  } = props

  const dispatch = useAppDispatch()
  const location = useLocation()
  const [middlewares, setMiddlewares] = useState<Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> | undefined>(undefined)
  const [
    regionCode,
    promotionMinLos,
    defaults,
    defaultAirport,
    isBedbankFlightBookingEnabled,
    isSpoofed,
    routerLocation,
  ] = useAppSelector(state => [
    state.geo.currentRegionCode,
    getPromotionMinLos(offer),
    getInitialBedbankOfferPageState(state, offer),
    getDefaultAirport(state),
    selectIsBedbankFlightBookingEnabled(state),
    state.auth.account.isSpoofed,
    state.router.location,
  ], shallowEqual)

  const isMobileScreen = useScreenSizeOnly('mobile')

  /************************************************************************************************/
  /* STOP! Do not lift `OfferPageState` into `<BedbankOfferPage/>`. Much like how `<OfferPage/>`  */
  /* defers state management to specific implementations (like `<FlashHotelContainer/>` or        */
  /* `<VillasPageContent/>`), the same applies to bedbank pages (with `<PropertyContainer/>` and  */
  /* `<BedbankOfferContainer/>`). If you think you _need_ to access `OfferPageState` in           */
  /* `<BedbankOfferPage/>`, speak to Xiao first.                                                  */
  /************************************************************************************************/
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    airport: defaultAirport,
    bedbankOffer: offer,
  }, middlewares)

  const {
    activeFilter,
    checkInDate,
    checkOutDate,
    rooms,
    airport,
    duration,
    selectedBedbankRoomId,
  } = state

  // Format occupancy for meta offers discount
  const occupancyString = rooms.map(generateOccupancyStringByRoom).join(',')

  useSyncBedbankTripItem(state, offer)

  const hasTransitionedFromMetasearch = useMetasearchSourceTransition()

  const { packages: metaOffersDiscountData, fetching: metaOffersDiscountLoading } = useMetaOffersDiscount({
    offerId,
    region: regionCode,
    checkIn: checkInDate || '',
    checkOut: checkOutDate || '',
    occupancy: occupancyString,
  })

  /**
   * 1. go to a YMAL recommendation
   * 2. Refresh page (clears redux)
   * 3. Go back
   * 4. FETCH_BEDBANK_OFFER action fires, but reducer already computed its initialState where bedbankoffer was initially undefined
   * 5. Fetch done -> loading indicator disappears, component crashes
   *
   * This useEffect is a workaround that.
   */
  useEffect(() => {
    if (offer) {
      stateDispatch({ type: OfferPageActions.setBedBankOffer, bedbankOffer: offer })
      if (defaults.duration) {
        stateDispatch({ type: OfferPageActions.setDuration, duration: defaults.duration })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer])

  useEffect(() => {
    if (isMobileScreen) {
      setMiddlewares(mobileVariantMiddlewares)
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [isMobileScreen])

  useEffect(() => {
    if (!activeFilter && location.hash === urlHashDatePicker) {
      dispatch(replace({ ...location, hash: undefined }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = !!offer?.flights?.flightsEnabled && isBedbankFlightBookingEnabled && occupancyNumber < 10

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: routerLocation.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      multiRoomMode: state.multiRoomMode,
    }).toString(), [checkInDate, checkOutDate, rooms, routerLocation.search, state.multiRoomMode])

  useEffect(() => {
    if (offer) {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, checkInDate, checkOutDate, rooms])

  useEffect(() => {
    if (!activeFilter || hasTransitionedFromMetasearch) {
      if (offer && checkInDate && checkOutDate && rooms?.length) {
        dispatch(fetchRatesForBedbankOffer(offer, rooms, checkInDate, checkOutDate, hasTransitionedFromMetasearch))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, checkInDate, checkOutDate, rooms, offer?.id, activeFilter, hasTransitionedFromMetasearch, metaOffersDiscountLoading])

  const checkInMoment = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOutMoment = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const selectedDuration = useMemo(() => checkOutMoment?.diff(checkInMoment, 'days'), [checkInMoment, checkOutMoment])
  const hotLead = useSaveHotLead({
    offerId,
    offerType: 'bedbank_hotel',
    checkInDate,
    checkOutDate,
    duration: selectedDuration,
    occupancy: rooms,
  })

  useEffect(() => {
    if (offer && hotLead?.checkIn && hotLead.checkOut) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useMountedEffect(() => {
    if (state.rooms && offer) {
      Analytics.trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  useMountedEffect(() => {
    if (selectedDuration && offer) {
      Analytics.trackEvent(offerPageDurationChangeEvent(offer, state))
    }
  }, [selectedDuration])

  useEffect(() => {
    if (offerId && config.BEDBANK_NEW_CALENDAR && duration && promotionMinLos <= duration) {
      dispatch(fetchCalendarForBedbankOffer(offerId, duration, selectedBedbankRoomId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, rooms, duration, promotionMinLos, selectedBedbankRoomId])

  const hasPromotions = (offer?.promotions ?? []).length > 0

  useEffect(() => {
    if (offer?.flights.airportCode && flightsEnabled && airport && duration && promotionMinLos <= duration && config.BEDBANK_NEW_CALENDAR) {
      dispatch(fetchFlightCalendar({
        ...getPromotionDefaultPeriod(),
        origin: airport.code,
        destination: offer.flights.airportCode,
        nights: duration,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, airport, flightsEnabled, promotionMinLos, duration])

  useEffect(() => {
    dispatch(replace({ ...routerLocation, search: queryString }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // ------------- LERE sticky footer -------------------
  const ymalStickyFooterHideTrigger = useInView({ triggerOnce: false })

  const canShowYmalFooter = ymalStickyFooterShowTrigger.inView && !ymalStickyFooterHideTrigger.inView && !isMobileScreen
  const showRecommendations = !!offer && !isSpoofed && !isMobileScreen

  const offerListFilters = useMemo<App.OfferListFilters>(() => ({
    propertyId: `bedbank:${offerId}`,
    searchNearby: true,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    rooms,
    lere: 'graph',
    baseOfferId: offerId,
  }), [offerId, checkInDate, checkOutDate, rooms])
  // ------------- END of LERE sticky footer -------------------

  return (
    <MetaOffersDiscountProvider offerId={offerId} packages={metaOffersDiscountData || []} loading={metaOffersDiscountLoading}>
      <OfferPageStateProvider state={state} dispatch={stateDispatch}>
        <HotelStructuredData offer={offer} />
        <ImmersiveSaveButtonPortal>
          <BookmarkButton offer={offer} />
        </ImmersiveSaveButtonPortal>
        <TripGuard>
          <SavePromptHotel offer={offer} />
        </TripGuard>
        <BedbankOfferContent
          flightsEnabled={flightsEnabled}
          ymalStickyFooterHideTriggerRef={ymalStickyFooterHideTrigger.ref}
          ymalStickyFooterShowTriggerRef={ymalStickyFooterShowTrigger.ref}
          canShowYmalFooter={canShowYmalFooter}
        />
        {!hasPromotions && showRecommendations && (
          <ErrorBoundary fallback={null}>
            <OfferYmalStickyFooter
              seedOffer={offer}
              filters={offerListFilters}
              show={canShowYmalFooter}
              initialShowDelayMs={2000}
              hideImmediately={!!activeFilter}
              pageSource="Bedbank"
            />
          </ErrorBoundary>
        )}
        {footerElement && hasPromotions && showRecommendations && createPortal(
          <ErrorBoundary fallback={null}>
            <OfferRecommendations
              title={intl.formatMessage(
                {
                  id: 'trrX8e3k2E',
                  defaultMessage: 'Similar to {hotelName}',
                  description: 'Title for similar offers section',
                },
                { hotelName: offer.property.name || 'this' },
              )}
              offerId={offerId}
              filters={offerListFilters}
              trackingDescription="Offer LE"
              enableDateCheckingForPriceDisplay
            />
          </ErrorBoundary>,
          footerElement,
        )}
      </OfferPageStateProvider>
    </MetaOffersDiscountProvider>
  )
}

export default React.memo(BedbankOfferContainer)
